/**
 * AMO option blocks runtime.
 *
 * AmoBridge — connects option blocks ([data-amo-step]) to the product's
 * add-to-cart form:
 *   - [data-amo-prop]    -> writes a line item property  (properties[Name])
 *   - [data-amo-variant] -> writes the variant id        (input[name="id"]) + price
 *   - [data-amo-upload]  -> moves the file input into the form, pet-count photo slots
 *   - [data-amo-required] -> blocks Add to cart until filled
 *
 * <amo-steps> — the step wizard: chips, progress bar, Back/Next. Uses one bridge
 * for the blocks inside it.
 *
 * Blocks placed OUTSIDE a wizard get their own bridge per section (auto-init),
 * so every option block also works on its own.
 *
 * No build step / imports: plain custom element + auto-init so it works
 * regardless of the import map.
 */
class AmoBridge {
  /**
   * @param {Element} root  element the events are delegated on
   * @param {Element|null} wizard  the <amo-steps> this bridge serves (null = standalone blocks)
   */
  constructor(root, wizard) {
    this.root = root;
    this.wizard = wizard;
    this.scope = root.closest('.shopify-section, dialog') || document;
    this.form = this._findForm();
    this._propInputs = new Map();
    this._uploads = [];
    this._photoGroups = [];
    const moneyEl = this.scope.querySelector('[data-amo-variants][data-money]');
    this._moneyFormat = (wizard && wizard.dataset.moneyFormat) || (moneyEl && moneyEl.dataset.money) || '';
    this._variants = this._parseVariants();
  }

  /* An element belongs to this bridge when its nearest wizard is ours. */
  owns(el) {
    return !!el && el.closest('amo-steps') === this.wizard;
  }

  steps() {
    return Array.from(this.root.querySelectorAll('[data-amo-step]')).filter((s) => this.owns(s));
  }

  _q(sel) {
    return Array.from(this.root.querySelectorAll(sel)).filter((el) => this.owns(el));
  }

  init() {
    this._bindOptionEvents();
    this.refresh();
  }

  /* Idempotent: picks up blocks added since the last run (theme editor). */
  refresh() {
    this._applyDefaults();
    this._syncAllProps();
    this._setupUploads();
  }

  /* ---------- variants ---------- */
  _parseVariants() {
    const el =
      (this.wizard && this.wizard.querySelector('[data-amo-variants]')) ||
      this.scope.querySelector('[data-amo-variants]');
    if (!el) return null;
    try {
      const list = JSON.parse(el.textContent);
      return Array.isArray(list) ? list : null;
    } catch (e) {
      return null;
    }
  }

  _formatMoney(cents) {
    if (!this._moneyFormat) return '';
    const amount = (cents / 100).toFixed(2);
    return this._moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)));
  }

  // Sale price, plus a struck-through compare-at when the variant is on sale.
  _priceHtml(price, compare) {
    const sale = this._formatMoney(price);
    if (!sale) return '';
    if (compare && compare > price) {
      return `<span class="amo-price-now">${sale}</span> <s class="amo-price-was">${this._formatMoney(compare)}</s>`;
    }
    return `<span class="amo-price-now">${sale}</span>`;
  }

  /* ---------- form discovery ---------- */
  _findForm() {
    return (
      this.scope.querySelector('form[data-type="add-to-cart-form"]') ||
      this.scope.querySelector('form[action^="/cart/add"]') ||
      document.querySelector('form[data-type="add-to-cart-form"]')
    );
  }

  /* ---------- validation ---------- */
  isStepValid(step) {
    if (!step) return true;
    for (const el of step.querySelectorAll('[data-amo-required="true"]')) {
      if (!(el.value || '').trim()) return false;
    }
    return true;
  }

  flagStep(step) {
    step?.classList.add('amo-step--error');
    step?.querySelector('[data-amo-required="true"]')?.focus?.();
  }

  /* The "required" rules must also hold when the real Add to cart is pressed.
     Capture phase so we run before the theme's product-form handler and can
     stop an incomplete submit. onInvalid(step) shows the shopper where. */
  bindFormGuard(onInvalid) {
    if (!this.form) return;
    this.form.addEventListener(
      'submit',
      (e) => {
        const bad = this.steps().find((s) => !this.isStepValid(s));
        if (!bad) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        onInvalid(bad);
      },
      { capture: true }
    );
  }

  /* ---------- file uploads ---------- */
  _setupUploads() {
    if (!this.form) return;
    const inputs = this._q('[data-amo-upload]').filter((i) => !i.__amoUpload);
    if (inputs.length) {
      // The form must be multipart for the file to be attached to the order.
      this.form.setAttribute('enctype', 'multipart/form-data');
    }
    inputs.forEach((input) => {
      input.__amoUpload = true;
      // Capture slot refs before moving the input into the form. Each photo slot
      // owns its own preview/flag/error (moving the input keeps label[for] working).
      const slot = input.closest('[data-amo-slot]') || input.closest('[data-amo-step]');
      const step = input.closest('[data-amo-step]');
      const preview = slot && slot.querySelector('[data-amo-upload-preview]');
      const flag = slot && slot.querySelector('[data-amo-upload-flag]');
      const error = slot && slot.querySelector('[data-amo-upload-error]');
      const maxBytes = (parseFloat(input.dataset.amoMaxMb) || 20) * 1024 * 1024;
      this.form.appendChild(input);
      this._uploads.push({ input, slot, preview, flag, error });
      input.addEventListener('change', () => {
        const files = Array.from(input.files || []);
        // Validate before accepting: Shopify rejects oversized uploads with an
        // opaque error, and HEIC/other formats break the embroidery workflow.
        const tooBig = files.some((f) => f.size > maxBytes);
        const badType = files.some((f) => f.type && !/^image\/(jpeg|png|webp)$/.test(f.type));
        if (tooBig || badType) {
          input.value = '';
          if (flag) flag.value = '';
          if (preview) preview.innerHTML = '';
          if (error) {
            error.textContent = tooBig
              ? input.dataset.amoSizeError || 'This file is too large.'
              : input.dataset.amoTypeError || 'Unsupported file type — please use JPG or PNG.';
            error.hidden = false;
          }
          return;
        }
        if (error) error.hidden = true;
        if (flag) flag.value = files.map((f) => f.name).join(', ');
        if (preview) this._renderUploadPreview(preview, files);
        step && step.classList.remove('amo-step--error');
      });
    });
    this._setupPhotoSlots();
  }

  /* Reveal as many photo slots as the chosen pet count (per group). */
  _setupPhotoSlots() {
    this._photoGroups = this._q('[data-amo-upload-group]').map((group) => ({
      countFrom: group.dataset.amoCountFrom || '',
      requirePhotos: group.dataset.amoRequiredPhotos === 'true',
      slots: Array.from(group.querySelectorAll('[data-amo-slot]')),
    }));
    this.refreshPhotoSlots();
  }

  // Pet count from the named property — our own blocks first, then anywhere in
  // the section (e.g. pets picked in the wizard, photos uploaded outside it).
  _petCount(name) {
    if (!name) return 1;
    const sel = `[data-amo-prop="${CSS.escape(name)}"]`;
    const holder = this._q(sel)[0] || this.scope.querySelector(sel);
    const m = holder && (holder.value || '').match(/\d+/);
    return m ? Math.max(1, parseInt(m[0], 10)) : 1;
  }

  refreshPhotoSlots() {
    this._photoGroups.forEach(({ countFrom, requirePhotos, slots }) => {
      const n = Math.min(this._petCount(countFrom), slots.length);
      slots.forEach((slot, i) => {
        const visible = i < n;
        slot.hidden = !visible;
        const flag = slot.querySelector('[data-amo-upload-flag]');
        if (flag) {
          if (visible && requirePhotos) flag.setAttribute('data-amo-required', 'true');
          else flag.removeAttribute('data-amo-required');
        }
        if (!visible) {
          // Clear a hidden slot so a stale file isn't submitted.
          const entry = this._uploads.find((u) => u.slot === slot);
          if (entry) {
            entry.input.value = '';
            if (entry.flag) entry.flag.value = '';
            if (entry.preview) entry.preview.innerHTML = '';
            if (entry.error) entry.error.hidden = true;
          }
        }
      });
    });
  }

  _renderUploadPreview(el, files) {
    el.innerHTML = '';
    files.forEach((f) => {
      if (!f.type || !f.type.startsWith('image/')) return;
      const img = document.createElement('img');
      img.className = 'amo-upload__thumb';
      img.src = URL.createObjectURL(f);
      img.onload = () => URL.revokeObjectURL(img.src);
      el.appendChild(img);
    });
  }

  /* ---------- option <-> form bridge ---------- */
  _bindOptionEvents() {
    // Choice buttons (swatches / option buttons)
    this.root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-amo-value]');
      if (!btn || !this.owns(btn)) return;
      const group = btn.closest('[data-amo-group]');
      if (!group) return;
      group.querySelectorAll('[data-amo-value]').forEach((b) => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');
      const holder = group.querySelector('[data-amo-prop], [data-amo-variant]');
      if (holder) {
        holder.value = btn.dataset.amoValue;
        holder.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const step = group.closest('[data-amo-step]');
      step?.classList.remove('amo-step--error');
      // Optional "picked value" subtitle
      const sel = step?.querySelector('[data-amo-selected]');
      if (sel) sel.textContent = btn.dataset.amoDisplay || btn.dataset.amoValue;
      if (btn.hasAttribute('data-amo-variant-id') || (holder && holder.hasAttribute('data-amo-variant'))) {
        this.applyVariant(btn);
      }
      this.refreshPhotoSlots();
    });

    // Free-text / native inputs
    this.root.addEventListener('input', (e) => {
      const el = e.target.closest('[data-amo-prop]');
      if (!el || !this.owns(el)) return;
      this._syncProp(el);
      this._updateCounter(el);
      el.closest('[data-amo-step]')?.classList.remove('amo-step--error');
    });
    this.root.addEventListener('change', (e) => {
      const el = e.target.closest('[data-amo-prop]');
      if (el && this.owns(el)) {
        this._syncProp(el);
        this.refreshPhotoSlots();
      }
    });
  }

  // Groups with data-amo-default start with a choice picked: the named one, else the first.
  _applyDefaults() {
    this._q('[data-amo-group][data-amo-default]').forEach((group) => {
      if (group.__amoDefault) return;
      group.__amoDefault = true;
      const btns = Array.from(group.querySelectorAll('[data-amo-value]'));
      const want = (group.dataset.amoDefault || '').trim().toLowerCase();
      const pick = btns.find((b) => b.dataset.amoValue.toLowerCase() === want) || btns[0];
      if (pick) pick.click();
    });
  }

  _syncAllProps() {
    this._q('[data-amo-prop]').forEach((el) => this._syncProp(el));
  }

  // Live "X/max characters" counter for text inputs that have a maxlength.
  _updateCounter(el) {
    const max = el.getAttribute && el.getAttribute('maxlength');
    if (!max) return;
    const field = el.closest('.amo-field');
    const counter = field && field.querySelector('[data-amo-counter]');
    if (counter) counter.textContent = `${(el.value || '').length}/${max} characters`;
  }

  _syncProp(el) {
    if (!this.form) return;
    const name = el.dataset.amoProp;
    if (!name) return;
    const fieldName = `properties[${name}]`;
    let input = this._propInputs.get(name);
    if (!input || !input.isConnected) {
      input = this.form.querySelector(`input[name="${CSS.escape(fieldName)}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = fieldName;
        this.form.appendChild(input);
      }
      this._propInputs.set(name, input);
    }
    input.value = el.value || '';
  }

  applyVariant(btn) {
    if (!this.form) return;
    let id = btn.dataset.amoVariantId;
    let priceHtml = btn.dataset.amoPriceHtml;

    // Multi-option products: resolve against ALL picked variant options in the
    // section (inside or outside a wizard), not just the one clicked, so e.g.
    // Size + Number of pets pick the correct variant. Falls back to the button's
    // own single-option variant when no matrix exists.
    if (this._variants && this._variants.length) {
      const picks = [];
      this.scope.querySelectorAll('[data-amo-variant][data-amo-option-position]').forEach((h) => {
        const pos = parseInt(h.dataset.amoOptionPosition, 10);
        if (pos && h.value) picks.push([pos, h.value]);
      });
      if (picks.length) {
        const match = this._variants.find((v) =>
          picks.every(([pos, val]) => (v.options || [])[pos - 1] === val)
        );
        if (match) {
          id = String(match.id);
          if (typeof match.price === 'number') priceHtml = this._priceHtml(match.price, match.compare_at) || priceHtml;
        }
      }
    }

    const idInput = this.form.querySelector('input[name="id"]');
    if (idInput && id) {
      idInput.value = id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    // Best-effort price refresh for the visible price (real charge comes from the variant).
    // amo-pdp02 listens to the id change itself and redraws its own price.
    if (priceHtml) {
      const target = this.scope.querySelector('product-price [ref="priceContainer"], [data-amo-price-target]');
      if (target) target.innerHTML = priceHtml;
    }
  }
}

class AmoSteps extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.bridge = new AmoBridge(this, this);
    this.steps = this.bridge.steps();
    this.chipsEl = this.querySelector('[data-amo-chips]');
    this.barFill = this.querySelector('[data-amo-bar-fill]');
    this.barThumb = this.querySelector('[data-amo-bar-thumb]');
    this.stepLabelEl = this.querySelector('[data-amo-step-label]');
    this.remainingEl = this.querySelector('[data-amo-step-remaining]');
    this.backBtn = this.querySelector('[data-amo-back]');
    this.nextBtn = this.querySelector('[data-amo-next]');
    this.stepWord = this.dataset.stepWord || 'Step';
    this.ofWord = this.dataset.ofWord || 'of';

    this.current = 0;
    this.furthest = 0;

    if (!this.steps.length) return;

    this.form = this.bridge.form;
    this._buildChips();
    this.bridge.init();
    this._bindNav();
    this.bridge.bindFormGuard((step) => {
      const i = this.steps.indexOf(step);
      this.go(i);
      this._flagInvalid(i);
    });
    this.go(0, { scroll: false });
  }

  /* ---------- chips / progress ---------- */
  _buildChips() {
    if (!this.chipsEl) return;
    this.chipsEl.innerHTML = '';
    this.chips = this.steps.map((step, i) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'amo-steps__chip';
      chip.textContent = step.dataset.amoLabel || `Step ${i + 1}`;
      chip.addEventListener('click', () => {
        if (i <= this.furthest) this.go(i);
      });
      this.chipsEl.appendChild(chip);
      return chip;
    });
  }

  _updateProgress() {
    const total = this.steps.length;
    const pct = total <= 1 ? 100 : (this.current / (total - 1)) * 100;
    if (this.barFill) this.barFill.style.width = pct + '%';
    if (this.barThumb) this.barThumb.style.insetInlineStart = pct + '%';
    if (this.stepLabelEl) {
      this.stepLabelEl.textContent = `${this.stepWord} ${this.current + 1} ${this.ofWord} ${total}`;
    }
    if (this.remainingEl) {
      const left = total - this.current - 1;
      this.remainingEl.textContent = left > 0 ? `${left} ${left === 1 ? 'step' : 'steps'} to go` : 'Last step';
    }
    this.chips?.forEach((chip, i) => {
      chip.classList.toggle('is-active', i === this.current);
      chip.classList.toggle('is-done', i < this.current || (i <= this.furthest && this._isValid(i)));
      chip.disabled = i > this.furthest;
    });
  }

  /* ---------- navigation ---------- */
  _bindNav() {
    this.backBtn?.addEventListener('click', () => this.go(this.current - 1));
    this.nextBtn?.addEventListener('click', () => {
      if (!this._isValid(this.current)) {
        this._flagInvalid(this.current);
        return;
      }
      if (this.current >= this.steps.length - 1) {
        this._finish();
        return;
      }
      this.go(this.current + 1);
    });
  }

  go(index, opts = {}) {
    const total = this.steps.length;
    this.current = Math.max(0, Math.min(index, total - 1));
    this.furthest = Math.max(this.furthest, this.current);
    this.steps.forEach((step, i) => {
      step.hidden = i !== this.current;
    });
    if (this.backBtn) this.backBtn.hidden = this.current === 0;
    const isLast = this.current >= total - 1;
    this.classList.toggle('amo-steps--last', isLast);
    if (this.nextBtn) {
      this.nextBtn.textContent = isLast ? this.dataset.finishLabel || 'Done' : this.dataset.nextLabel || 'Next';
    }
    this._updateProgress();
    this._scrollChipIntoView(opts.scroll === false);
    if (opts.scroll !== false) this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /* Keep the active step chip in view (the strip shows ~4 at a time). */
  _scrollChipIntoView(instant) {
    const chip = this.chips && this.chips[this.current];
    if (!chip || !this.chipsEl) return;
    if (this.classList.contains('amo-steps--whole')) return this._fitChips();
    const c = this.chipsEl;
    const left = chip.offsetLeft - (c.clientWidth - chip.clientWidth) / 2;
    c.scrollTo({ left: Math.max(0, left), behavior: instant ? 'auto' : 'smooth' });
  }

  /* "Whole chips only": one row, never a half-cut chip. When they don't all fit, show the
     widest run of whole chips around the current step (future steps first); it shifts as you go. */
  _fitChips() {
    const c = this.chipsEl;
    this.chips.forEach((ch) => ch.classList.remove('is-off'));
    c.scrollLeft = 0;
    if (c.scrollWidth <= c.clientWidth + 1) return;
    const cs = getComputedStyle(c);
    const gap = parseFloat(cs.columnGap) || 0;
    const avail = c.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    const w = this.chips.map((ch) => ch.getBoundingClientRect().width);
    let a = this.current;
    let b = this.current;
    let used = w[this.current];
    for (let grew = true; grew; ) {
      grew = false;
      if (b + 1 < w.length && used + gap + w[b + 1] <= avail) { b += 1; used += gap + w[b]; grew = true; }
      if (a > 0 && used + gap + w[a - 1] <= avail) { a -= 1; used += gap + w[a]; grew = true; }
    }
    this.chips.forEach((ch, i) => ch.classList.toggle('is-off', i < a || i > b));
    c.classList.toggle('has-before', a > 0);
    c.classList.toggle('has-after', b < w.length - 1);
    if (!this._fitBound) {
      this._fitBound = true;
      let t;
      window.addEventListener('resize', () => { clearTimeout(t); t = setTimeout(() => this._fitChips(), 120); });
      document.fonts?.ready.then(() => this._fitChips());
    }
  }

  _finish() {
    // Reveal the buy area (if it was hidden behind the wizard) and notify.
    this.classList.add('amo-steps--complete');
    this.dispatchEvent(new CustomEvent('amo:steps:complete', { bubbles: true }));
    const scope = this.closest('.shopify-section, dialog') || document;
    const buy = scope.querySelector('[data-amo-buy-area]');
    if (buy) {
      buy.hidden = false;
      buy.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // No dedicated buy area: send the shopper to the real Add to cart so "Done"
    // actually leads somewhere.
    const atc =
      (this.form && (this.form.querySelector('[name="add"]') || this.form.querySelector('button[type="submit"]'))) ||
      scope.querySelector('add-to-cart button, button[name="add"]');
    if (atc) {
      atc.scrollIntoView({ behavior: 'smooth', block: 'center' });
      atc.focus?.({ preventScroll: true });
    }
  }

  /* ---------- validation ---------- */
  _isValid(index) {
    return this.bridge.isStepValid(this.steps[index]);
  }

  _flagInvalid(index) {
    this.bridge.flagStep(this.steps[index]);
  }
}

if (!customElements.get('amo-steps')) {
  customElements.define('amo-steps', AmoSteps);
}

/* ---------- standalone blocks (outside any wizard) ---------- */
function amoInitStandalone(root) {
  const scopes = new Set();
  (root || document).querySelectorAll('[data-amo-step]').forEach((step) => {
    if (step.closest('amo-steps')) return;
    scopes.add(step.closest('.shopify-section, dialog') || document.body);
  });
  scopes.forEach((scope) => {
    if (scope.__amoBridge) {
      scope.__amoBridge.refresh();
      return;
    }
    const bridge = new AmoBridge(scope, null);
    scope.__amoBridge = bridge;
    bridge.init();
    bridge.bindFormGuard((step) => {
      bridge.flagStep(step);
      step.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  });
}

if (!window.__amoStandalone) {
  window.__amoStandalone = true;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => amoInitStandalone(document));
  } else {
    amoInitStandalone(document);
  }
  document.addEventListener('shopify:section:load', (e) => amoInitStandalone(e.target));
  document.addEventListener('shopify:block:select', () => amoInitStandalone(document));
}
